import { TypedDocumentNode, gql } from '@apollo/client';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import {
  Button,
  Props as ButtonProps,
} from '@sorare/core/src/atoms/buttons/Button';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { UnvailableButtonForAndroid } from '@sorare/core/src/components/BuyableFeature';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { glossary, mobileApp } from '@sorare/core/src/lib/glossary';

import {
  PrimaryOfferBuyFieldWrapper,
  Props as PrimaryOfferBuyFieldWrapperProps,
} from './PrimaryOfferBuyFieldWrapper';
import { PrimaryBuyBuyField_anyCard } from './__generated__/index.graphql';

export type Props = Omit<PrimaryOfferBuyFieldWrapperProps, 'children'> &
  Omit<ButtonProps, 'loading'> & { cta?: ReactNode };

export const PrimaryBuyBuyField = ({
  card,
  primaryBuyConfirmationOptions,
  onSuccess,
  onBuyConfirmationClose,
  origin,
  gameSlug,
  showBuyConfirmation = true,
  onClick,
  onlyCreditCards = false,
  conversionCreditDisclaimer,
  defaultConversionCredit,
  composeSessionId,
  cta,
  ...props
}: Props) => {
  const {
    flags: { useAbTestInstantBuyCtaLabel = false },
  } = useFeatureFlags();

  const { isAndroidApp } = useIsMobileApp();

  const ctaNode = (() => {
    if (cta) return cta;
    if (useAbTestInstantBuyCtaLabel) {
      if (card.sport === Sport.FOOTBALL) {
        return (
          <FormattedMessage
            id="instantBuy.cta.football"
            defaultMessage="Recruit"
          />
        );
      }
      return (
        <FormattedMessage
          id="instantBuy.cta.us-sports"
          defaultMessage="Add to roster"
        />
      );
    }
    return <FormattedMessage {...glossary.buy} />;
  })();

  if (!card.latestPrimaryOffer?.price) return null;

  return (
    <PrimaryOfferBuyFieldWrapper
      card={card}
      primaryBuyConfirmationOptions={primaryBuyConfirmationOptions}
      onSuccess={onSuccess}
      onBuyConfirmationClose={onBuyConfirmationClose}
      origin={origin}
      gameSlug={gameSlug}
      showBuyConfirmation={showBuyConfirmation}
      onlyCreditCards={onlyCreditCards}
      conversionCreditDisclaimer={conversionCreditDisclaimer}
      defaultConversionCredit={defaultConversionCredit}
      composeSessionId={composeSessionId}
      onClick={onClick}
    >
      {({ onClick: onBuyClick, loading, disabled }) => {
        if (isAndroidApp && disabled) {
          return (
            <UnvailableButtonForAndroid
              cardSlug={card.slug}
              color="primary"
              {...props}
            />
          );
        }
        if (disabled) {
          return (
            <Button color="primary" {...props} disabled>
              <FormattedMessage {...mobileApp.unavailable} />
            </Button>
          );
        }
        return (
          <LoadingButton
            color="primary"
            onClick={onBuyClick}
            {...props}
            loading={loading}
          >
            {ctaNode}
          </LoadingButton>
        );
      }}
    </PrimaryOfferBuyFieldWrapper>
  );
};

PrimaryBuyBuyField.fragments = {
  anyCard: gql`
    fragment PrimaryBuyBuyField_anyCard on AnyCardInterface {
      slug
      sport
      latestPrimaryOffer {
        id
        appleProductId
      }
      ...PrimaryOfferBuyFieldWrapper_anyCard
    }
    ${PrimaryOfferBuyFieldWrapper.fragments.anyCard}
  ` as TypedDocumentNode<PrimaryBuyBuyField_anyCard>,
};
